/**
 * A register dataset key, in the words the interface speaks.
 *
 * `ArSyncRun.dataset` stores the importer's own key - `general`, `tax-history`,
 * `changes` - and two screens print it: the register panel's "running" line and
 * the shell's background card. The card printed it RAW, so a nightly import
 * announced itself in the middle of an Estonian interface as "general", next to
 * a progress bar and a spinner that were otherwise fully translated.
 *
 * The wording itself lives in the catalogue (`ariregister.sync.ds.*`). What this
 * file adds is the fallback, and it is the whole reason the lookup is not a bare
 * `t()` call at each site: `translate()` answers a key it cannot find with the
 * key itself, so a dataset the server gained before its wording landed here
 * would print `ariregister.sync.ds.beneficials` on screen. That is strictly
 * worse than the raw word it was meant to replace - one is an unfamiliar noun,
 * the other is a bug on display. When the lookup does not resolve, the raw key
 * stays.
 *
 * Pure, and deliberately React-free: the caller passes its own `t`.
 */

type T = (key: string, vars?: Record<string, string | number>) => string;

const PREFIX = "ariregister.sync.ds.";

export function datasetLabel(t: T, dataset: string | null | undefined): string {
  const raw = (dataset ?? "").trim();
  if (!raw) return "";
  const key = `${PREFIX}${raw}`;
  const label = t(key);
  // `translate()` returns the key when nothing matched - that is the miss.
  return label === key ? raw : label;
}
